// Cloudflare Workers API client for TraderTekno
import type { Instrument, EnsembleSignalsResponse } from '../../types/ai';
import { getAPIUrl } from '../../config/api.config';
import { logger } from '../../utils/logger';
import { trackAPIError } from '../../utils/ga4';
import { notifySessionExpired, notifyAccountSuspended } from './authTokenManager';

export interface CloudflareAPIConfig {
  baseURL: string;
  timeout?: number;
}

export interface MarketDataResponse {
  success: boolean;
  data: MarketData[];
  timestamp: string;
  message: string;
}

export interface MarketData {
  instrument: Instrument;
  bid: number;
  ask: number;
  spread: number;
  change: number;
  changePercent: number;
  volume: number;
  timestamp: string;
  session: 'sydney' | 'tokyo' | 'london' | 'newyork' | 'closed';
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  nameId?: string;
  price: number;
  currency: 'IDR';
  period: string;
  description?: string;
  features: string[];
  featuresId?: string[];
  discount?: {
    firstMonth: number;
    originalPrice: number;
    discountedPrice: number;
  };
  limits: {
    signalsPerDay: number;
    instruments: number;
  };
  popular?: boolean;
}

export interface PaymentRequest {
  userId: string;
  planId: string;
  paymentMethod?: string;
  customerDetails: {
    name: string;
    email: string;
    phone: string;
  };
}

export interface DuitkuPaymentMethod {
  paymentMethod: string;
  paymentName: string;
  paymentImage: string;
  totalFee: string;
}

export interface PaymentResponse {
  success: boolean;
  data: {
    payment_url: string;
    reference: string;
    order_id: string;
  };
  message: string;
}

export interface TradingSession {
  start: string;
  end: string;
  name: string;
}

export interface TradingSessionsResponse {
  success: boolean;
  data: {
    sessions: Record<string, TradingSession>;
    current: {
      session: string;
      time: string;
      isWeekend: boolean;
    };
  };
  message: string;
}

// AI Signal types
export interface AISignal {
  id: string;
  instrument: string;
  direction: 'NAIK' | 'TURUN' | 'NETRAL';
  confidence: number;
  entry: number | null;
  stopLoss: number | null;
  takeProfit: number | null;
  timeframe: string;
  reasoning: string;
  riskReward: number;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH';
  source?: string;
  createdAt: string;
  expiresAt: string;
  currentPrice?: number | null;
  spread?: number;
  session?: string;
  aiModel?: string;
  // Additional fields from stock analysis
  fundamentalScore?: number;
  technicalScore?: number;
  confluenceScore?: number;
  keyHighlights?: string[];
  // Pre-computed target price and upside (from ai_signals fast path)
  targetPrice?: number | null;
  potentialUpside?: number | null;
  // Price source indicator for UI display
  priceSource?: 'YAHOO_FINANCE_LIVE' | 'ANALYSIS_FALLBACK' | 'PRECOMPUTED';
  metadata?: {
    aiModel: string;
    marketConditions: {
      volatility: 'LOW' | 'MEDIUM' | 'HIGH';
      trend: 'BULLISH' | 'BEARISH' | 'SIDEWAYS';
      sessionActive: boolean;
    };
  };
}

export interface AISignalsResponse {
  success: boolean;
  data: {
    signals: AISignal[];
    marketOutlook: string;
    riskWarning: string;
    generatedAt: string;
    aiProvider: string;
    totalSignals: number;
    averageConfidence: number;
  };
  cached: boolean;
}

export interface MarketSummary {
  summary: string;
  timestamp: string;
  marketConditions: {
    instrument: string;
    change: number;
    session: string;
  }[];
}

export interface MarketSummaryResponse {
  success: boolean;
  data: MarketSummary;
  cached: boolean;
}

// User stats types
export interface UserStats {
  userId: string;
  totalSignalsFollowed: number;
  winningSignals: number;
  losingSignals: number;
  activeSignals: number;
  totalProfitPips: number;
  winRate: number;
  averageRiskReward: number;
  monthlyProfit: number;
  lastUpdated: string;
}

export interface UserStatsResponse {
  success: boolean;
  data: UserStats;
}

export class CloudflareAPI {
  private config: CloudflareAPIConfig;

  constructor(config: CloudflareAPIConfig) {
    this.config = {
      timeout: 30000, // Increased to 30 seconds for AI signal generation
      ...config
    };
  }

  private async request<T>(endpoint: string, options: RequestInit = {}, customTimeout?: number): Promise<T> {
    const url = `${this.config.baseURL}${endpoint}`;
    const timeout = customTimeout ?? this.config.timeout;
    
    // Get Firebase auth token from token manager
    let authToken: string | undefined;
    try {
      const { getAuthToken, getAuthTokenAsync } = await import('./authTokenManager');

      // First try synchronous get (fast path)
      authToken = getAuthToken() ?? undefined;

      // If not available, try async fallback
      if (!authToken) {
        authToken = (await getAuthTokenAsync()) ?? undefined;
      }

      if (authToken) {
        logger.debug('API request with auth token');
      } else {
        logger.debug('API request without auth token');
      }
    } catch (error) {
      logger.warn('Failed to get auth token for API request', { error: String(error) });
    }
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Origin': window.location.origin,
        ...((options.headers as Record<string, string>) || {}),
      };
      
      // Add authorization header if token is available
      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }
      
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        mode: 'cors',
        credentials: 'include',
        headers,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' })) as { error?: string; errorCode?: string; details?: string; message?: string };

        // Build error message including details if available
        const errorMessage = errorData.details
          ? `${errorData.error || errorData.message || 'Error'}: ${errorData.details}`
          : errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`;

        trackAPIError(endpoint, response.status, errorMessage);

        // For 401 errors, sign out user and redirect to login
        if (response.status === 401) {
          notifySessionExpired();
          throw new Error('401: Session expired');
        }

        // For 403 errors, include the error code for better handling
        if (response.status === 403) {
          // UU PDP: kalau akun ditangguhkan (gdpr/erase suspended) → trigger global handler
          const errCode = errorData.errorCode || (errorData as { code?: string }).code;
          if (errCode === 'ACCOUNT_SUSPENDED') {
            notifyAccountSuspended();
          }
          const error = new Error(errorMessage) as Error & { errorCode?: string; status?: number };
          if (errorData.errorCode) {
            error.errorCode = errorData.errorCode;
          }
          error.status = response.status;
          throw error;
        }

        throw new Error(errorMessage);
      }

      const data = await response.json() as { success: boolean; error?: string; details?: string; message?: string };

      if (!data.success) {
        // Include details in error message if available
        const errorMessage = data.details
          ? `${data.error || data.message || 'Error'}: ${data.details}`
          : data.error || data.message || 'API request failed';
        throw new Error(errorMessage);
      }

      return data as T;
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout');
        }
        throw error;
      }
      
      throw new Error('Unknown error occurred');
    }
  }

  // Market Data APIs
  async getMarketData(): Promise<MarketData[]> {
    const response = await this.request<MarketDataResponse>('/api/market/data');
    return response.data;
  }

  async getInstrumentData(instrument: Instrument): Promise<MarketData> {
    const response = await this.request<{ success: boolean; data: MarketData; message: string }>(
      `/api/market/data/${instrument}`
    );
    return response.data;
  }

  async getTradingSessions(): Promise<TradingSessionsResponse['data']> {
    const response = await this.request<TradingSessionsResponse>('/api/market/sessions');
    return response.data;
  }

  // Payment APIs
  async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    const response = await this.request<{ success: boolean; data: SubscriptionPlan[]; message: string }>(
      '/api/payments/plans'
    );
    return response.data;
  }

  async createPaymentTransaction(paymentRequest: PaymentRequest): Promise<PaymentResponse['data']> {
    const response = await this.request<PaymentResponse>('/api/payments/create-transaction', {
      method: 'POST',
      body: JSON.stringify(paymentRequest),
    });
    return response.data;
  }

  async getPaymentMethods(amount: number): Promise<DuitkuPaymentMethod[]> {
    const response = await this.request<{ success: boolean; data: DuitkuPaymentMethod[] }>(
      `/api/payments/payment-methods?amount=${amount}`
    );
    return response.data;
  }

  async getTransactionStatus(orderId: string): Promise<unknown> {
    const response = await this.request<{ success: boolean; data: unknown; message: string }>(
      `/api/payments/status/${orderId}`
    );
    return response.data;
  }

  // AI APIs
  async getAISignals(): Promise<AISignalsResponse['data']> {
    // Always use regular signals endpoint - tier-based routing is handled by backend
    const response = await this.request<AISignalsResponse>('/api/ai/signals', {}, 15000);
    return response.data;
  }

  // Ensemble AI Signals - Multi-model consensus (Professional+ tier only)
  async getEnsembleSignals(): Promise<EnsembleSignalsResponse['data'] | null> {
    try {
      // Extended timeout for ensemble processing (multiple AI models)
      const response = await this.request<EnsembleSignalsResponse>('/api/ai/signals/ensemble', {}, 60000);
      return response.data;
    } catch (error) {
      logger.warn('Ensemble signals not available', { error: String(error) });
      return null;
    }
  }

  // NOTE: Tier-based routing is handled by the backend
  // The /api/ai/signals endpoint automatically returns appropriate signals based on user's subscription tier
  // Free tier: Standard signals
  // Professional+: Ensemble signals (if they upgrade)

  async getMarketSummary(): Promise<MarketSummary> {
    // Path BUKAN /api/ai/market-summary — handler-nya ter-mount di bawah sub-router
    // '/analysis' (api/ai/index.ts: app.route('/analysis', createMarketAnalysisHandlers())).
    // Memanggil path lama balas 404, dan dashboard menampilkan "Ringkasan pasar belum
    // tersedia saat ini" seolah datanya yang kosong — padahal endpoint-nya salah alamat.
    // Diverifikasi 18 Agustus 2026: /api/ai/market-summary -> 404,
    // /api/ai/analysis/market-summary -> 401 (endpoint ada, hanya butuh auth).
    const response = await this.request<MarketSummaryResponse>('/api/ai/analysis/market-summary');
    return response.data;
  }


  // User APIs
  async getUserStats(userId: string): Promise<UserStats> {
    const response = await this.request<UserStatsResponse>(`/api/users/${userId}/stats`);
    return response.data;
  }

  // Health check
  async getHealthStatus(): Promise<unknown> {
    return this.request<unknown>('/api/status');
  }

  // Get base URL for external use
  getBaseUrl(): string {
    return this.config.baseURL;
  }

  // Generic GET request method
  async get<T = { success: boolean; data: unknown }>(endpoint: string, customTimeout?: number): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' }, customTimeout);
  }

  // Generic POST request method
  async post<T = { success: boolean; data: unknown }>(endpoint: string, body?: unknown, customTimeout?: number): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }, customTimeout);
  }

  // Generic DELETE request method
  async delete<T = { success: boolean }>(endpoint: string, customTimeout?: number): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' }, customTimeout);
  }
}

// Create singleton instance
// Environment-based API URL configuration using centralized config
const getBaseURL = () => {
  return getAPIUrl();
};

const cloudflareAPI = new CloudflareAPI({
  baseURL: getBaseURL()
});

export default cloudflareAPI;